import { Hours } from '../features/schedule/types';
import { AbsenceBehavior } from '../features/types/types';
import type { TimeReportItem } from '../features/time-report/types';

const isWillWorkAbsence = (item: TimeReportItem): boolean => (
    item.absenceBehavior === AbsenceBehavior.WillWork
);

export const getTimeReportWorkHours = (items: TimeReportItem[]): number => (
    items.reduce((sum, item) => sum + item.workHours, 0)
);

export const getTimeReportAbsenceHours = (items: TimeReportItem[]): number => (
    items.reduce((sum, item) => sum + item.absenceHours, 0)
);

export const getTimeReportTotalHours = (items: TimeReportItem[]): number => (
    getTimeReportWorkHours(items) + getTimeReportAbsenceHours(items)
);

export const getTimeReportApprovedHours = (items: TimeReportItem[]): number => (
    items
        .filter((item) => !isWillWorkAbsence(item)
            && (!item.date || (item.timeBankHours ?? 0) >= 0))
        .reduce((sum, item) => sum + item.approvedHours, 0)
);

export const getTimeReportBilledHours = (items: TimeReportItem[]): number => (
    items
        .filter((item) => !isWillWorkAbsence(item))
        .reduce((sum, item) => sum + item.billedHours, 0)
);

export const getTimeReportOvertimeHours = (items: TimeReportItem[]): number => (
    items.reduce((sum, item) => sum + item.overtimes, 0)
);

export const getTimeReportPlannedHours = (items: TimeReportItem[]): number => {
    const valuesByEmployeeDay = items.reduce<Map<string, number>>((map, item) => {
        const key = `${item.id}|${item.date}`;
        if (!map.has(key)) {
            map.set(key, item.plannedHours);
        }

        return map;
    }, new Map());

    return Array.from(valuesByEmployeeDay.values()).reduce((sum, value) => sum + value, 0);
};

export const getTimeReportTimeBankBalance = (items: TimeReportItem[]): number | undefined => {
    const hasTimeBankHours = items.some((item) => item.timeBankHours !== undefined);
    return hasTimeBankHours
        ? items.reduce((sum, item) => sum + (item.timeBankHours ?? 0), 0)
        : undefined;
};

export const getTimeReportSummaryHours = (items: TimeReportItem[]): Hours => {
    const workHours = getTimeReportWorkHours(items);
    const absenceHours = getTimeReportAbsenceHours(items);

    return {
        totalHours: workHours + absenceHours,
        approved: getTimeReportApprovedHours(items),
        billed: getTimeReportBilledHours(items),
        workHours,
        absenceHours,
        planned: 0
    };
};
